import { EXERCISES } from '@/data/exercises';
import { LEVEL_REPS_FACTOR, LEVEL_VOLUME_FACTOR, PRIORITY_ZONE_BONUS_SET } from '@/data/constants';
import type { EquipmentId, Exercise, FitnessLevel, MicroWorkout, ProgramDay, ProgramItem, ZoneId } from '@/types';

/**
 * Item de séance après application des règles d'adaptation (variantes par niveau,
 * substitutions matériel, pondération zones prioritaires, multiplicateurs de volume).
 */
export interface ResolvedItem extends ProgramItem {
  /** Exercice finalement utilisé (peut différer de `ex` si substitution ou variante de niveau) */
  resolvedEx: string;
  /** Données complètes de l'exercice résolu */
  exercise: Exercise;
  /** Si l'exercice résolu est une substitution matériel, on garde l'origine (pour la traçabilité) */
  substitutedFor?: string;
  /** Si l'exercice résolu est une variante de niveau, on garde l'exo nominal du programme */
  levelVariantOf?: string;
  /** Indique si une série bonus a été appliquée (zones prioritaires) */
  bonusApplied?: boolean;
}

export interface ResolvedDay {
  titre: string;
  tag: ProgramDay['tag'];
  items: ResolvedItem[];
}

/** Vérifie si tous les équipements requis sont présents. */
const hasAllEquipment = (required: EquipmentId[], owned: EquipmentId[]): boolean =>
  required.every(eq => owned.includes(eq));

/**
 * Applique la variante de niveau d'un exercice si elle existe ET est faisable
 * avec l'équipement disponible. Renvoie l'exo de remplacement ou l'original.
 */
const applyLevelVariant = (
  ex: Exercise,
  level: FitnessLevel,
  ownedEquipment: EquipmentId[],
): { exercise: Exercise; levelVariantOf?: string } => {
  const targetId = ex.levelVariants?.[level];
  if (!targetId) return { exercise: ex };
  const target = EXERCISES[targetId];
  if (!target) return { exercise: ex };
  if (!hasAllEquipment(target.requires, ownedEquipment)) return { exercise: ex };
  return { exercise: target, levelVariantOf: ex.id };
};

/** Compte combien de zones de l'exercice sont marquées prioritaires. */
const priorityZoneScore = (zones: ZoneId[], priority: ZoneId[]): number =>
  zones.reduce((acc, z) => acc + (priority.includes(z) ? 1 : 0), 0);

/**
 * Résout l'exercice à utiliser selon le matériel disponible.
 * - Si l'exo demandé est ok, on le garde.
 * - Sinon, on parcourt ses substitutes ; on ne retient que ceux dont la condition
 *   `no_<eq>` correspond à un équipement effectivement absent ET dont l'exo cible
 *   est lui-même réalisable.
 * - Parmi les substituts éligibles, on prend celui qui maximise le score de zones
 *   prioritaires (puis ordre déclaré).
 *
 * Retourne `null` si même les substituts ne sont pas réalisables.
 */
export const resolveExercise = (
  requested: Exercise,
  ownedEquipment: EquipmentId[],
  priorityZones: ZoneId[],
): { exercise: Exercise; substitutedFor?: string } | null => {
  if (hasAllEquipment(requested.requires, ownedEquipment)) {
    return { exercise: requested };
  }

  const subs = requested.substitutes ?? [];
  type Candidate = { exercise: Exercise; score: number; order: number };
  const candidates: Candidate[] = [];

  subs.forEach((sub, order) => {
    const missingEq = sub.when.replace(/^no_/, '') as EquipmentId;
    if (ownedEquipment.includes(missingEq)) return; // condition non remplie
    const target = EXERCISES[sub.use];
    if (!target) return;
    if (!hasAllEquipment(target.requires, ownedEquipment)) return;
    candidates.push({ exercise: target, score: priorityZoneScore(target.zones, priorityZones), order });
  });

  if (candidates.length === 0) return null;
  candidates.sort((a, b) => (b.score - a.score) || (a.order - b.order));
  const winner = candidates[0]!;
  return { exercise: winner.exercise, substitutedFor: requested.id };
};

/**
 * Ajuste la chaîne `r` (reps) selon le niveau, en gardant le format d'origine.
 * - "12" → "16" (advanced) ou "10" (beginner)
 * - "10-12" → "14-16" / "8-10"
 * - "AMRAP", "8 / jambe", "10 / jambe" → inchangé
 */
const scaleRepsString = (r: string | undefined, level: FitnessLevel): string | undefined => {
  if (!r) return r;
  if (level === 'intermediate') return r;
  // Ne touche pas si format non purement numérique
  if (/[a-z]/i.test(r) && !/^\s*\d+\s*-\s*\d+\s*$/.test(r)) return r;
  if (/\//.test(r)) return r;

  const factor = LEVEL_REPS_FACTOR[level];
  return r.replace(/\d+/g, n => String(Math.max(1, Math.round(Number(n) * factor))));
};

/**
 * Construit la séance résolue selon le profil utilisateur :
 *   1. Variante de niveau (pompes_genoux → pompes pour intermediate, → pompes_diamant pour advanced)
 *   2. Substitution matériel (si l'exo final n'est pas réalisable)
 *   3. Bonus +1 série sur exos zones prioritaires (séances muscu uniquement)
 *   4. Multiplicateur de volume global selon niveau (-30% beginner, +30% advanced)
 *   5. Mise à l'échelle des reps numériques
 */
export const buildResolvedDay = (
  day: ProgramDay,
  ownedEquipment: EquipmentId[],
  priorityZones: ZoneId[],
  level: FitnessLevel = 'intermediate',
): ResolvedDay => {
  const items: ResolvedItem[] = [];
  const volumeFactor = LEVEL_VOLUME_FACTOR[level];

  const levelRank: Record<FitnessLevel, number> = { beginner: 0, intermediate: 1, advanced: 2 };

  for (const item of day.items) {
    // 0. Filtre par niveau minimum (accessoires advanced exclus pour beginner/intermediate)
    if (item.minLevel && levelRank[level] < levelRank[item.minLevel]) continue;

    const original = EXERCISES[item.ex];
    if (!original) continue;

    // 1. Variante de niveau (si dispo et faisable avec le matériel)
    const variant = applyLevelVariant(original, level, ownedEquipment);

    // 2. Substitution matériel sur l'exo (variant ou original)
    const resolved = resolveExercise(variant.exercise, ownedEquipment, priorityZones);
    if (!resolved) continue;

    const ex = resolved.exercise;
    const touchesPriority = priorityZoneScore(ex.zones, priorityZones) > 0;
    const bonusEligible = day.tag === 'muscu' && !ex.guide && !ex.t && touchesPriority;

    // 3+4. Volume = item.s + bonus zone prio, multiplié par le facteur de niveau
    const baseSets = item.s + (bonusEligible ? PRIORITY_ZONE_BONUS_SET : 0);
    const scaledSets = day.tag === 'repos' || ex.guide
      ? baseSets // ne touche pas aux jours de repos / routines guidées
      : Math.max(1, Math.round(baseSets * volumeFactor));

    // 5. Reps mises à l'échelle
    const scaledReps = scaleRepsString(item.r, level);

    items.push({
      ...item,
      ex: item.ex,
      r: scaledReps,
      s: scaledSets,
      resolvedEx: ex.id,
      exercise: ex,
      ...(resolved.substitutedFor ? { substitutedFor: resolved.substitutedFor } : {}),
      ...(variant.levelVariantOf ? { levelVariantOf: variant.levelVariantOf } : {}),
      ...(bonusEligible ? { bonusApplied: true } : {}),
    });
  }

  return { titre: day.titre, tag: day.tag, items };
};

/** Variante pour micro-séance (même logique). */
export const buildResolvedMicro = (
  micro: MicroWorkout,
  ownedEquipment: EquipmentId[],
  priorityZones: ZoneId[],
  level: FitnessLevel = 'intermediate',
): ResolvedDay => buildResolvedDay({ titre: micro.name, tag: 'muscu', items: micro.items }, ownedEquipment, priorityZones, level);

/**
 * Estimation grossière de la durée d'une séance résolue (en secondes).
 * Pour les exos non temporels, on suppose ~45s par série + repos entre séries.
 */
export const estimateDurationSec = (resolved: ResolvedDay): number => {
  const ESTIMATED_PER_SET = 45;
  return resolved.items.reduce((acc, it) => {
    const work = it.dur ? it.dur * it.s : ESTIMATED_PER_SET * it.s;
    const rest = (it.rest ?? 0) * Math.max(0, it.s - 1);
    return acc + work + rest;
  }, 0);
};

/** Nombre total de séries cumulées d'une séance résolue. */
export const totalSets = (resolved: ResolvedDay): number =>
  resolved.items.reduce((acc, it) => acc + it.s, 0);
